/**
 * Asset loading.
 *
 * Everything is optional. The app is designed to boot with zero baked assets -
 * `buildRoad.ts` sweeps a spec-accurate road at runtime - and to pick up
 * `road.glb`, `mid.glb`, `far.glb` and `props.glb` as the pipeline produces
 * them. That decoupling is what keeps the runtime work (W5) from being blocked
 * behind the asset work (W1-W4).
 *
 * The glTF/Draco/KTX2 loaders are ~1.5 MB of JS and WASM and are imported
 * DYNAMICALLY, only once a `.glb` is known to exist. Until Stage C-D produce
 * one, first load never touches them - which is most of how the sub-10-second
 * cold start is held against a competitor that takes one to two minutes.
 *
 * SECURITY: asset paths are a fixed allowlist of same-origin relative URLs.
 * Nothing here ever takes a URL from user input, a query string or fetched
 * content, so there is no path by which a crafted string becomes a network
 * request. Keep it that way - the CSP is `default-src 'none'`, and a remote
 * asset host would mean widening it.
 */
import type { Group, WebGLRenderer } from 'three';
import type { BuildingFeatureCollection } from '../scene/buildings';
import type { TerrainGridData } from '../road/terrain';

/** The only assets this app will ever request. */
export const ASSETS = {
  road: 'assets/dist/road.glb',
  mid: 'assets/dist/mid.glb',
  far: 'assets/dist/far.glb',
  props: 'assets/dist/props.glb',
  buildings: 'assets/buildings.geojson',
  terrain: 'assets/terrain-grid.json',
} as const;

export type AssetName = keyof typeof ASSETS;

/** Remains off until `npm run test:parity` is green for the candidate GLB. */
export const BAKED_ROAD_ENABLED = import.meta.env.VITE_ENABLE_BAKED_ROAD === '1';

/** Decoders are self-hosted so the CSP needs no CDN allowlisted. */
const DRACO_PATH = 'decoders/draco/gltf/';
const KTX2_PATH = 'decoders/basis/';

export interface LoadedAssets {
  road: Group | null;
  mid: Group | null;
  far: Group | null;
  props: Group | null;
  /** Validated source footprints; geometry is still built at runtime. */
  buildings: BuildingFeatureCollection | null;
  terrain: TerrainGridData | null;
}

export type LoadProgress = (fraction: number, message: string) => void;

interface GltfStack {
  load(url: string): Promise<Group>;
  dispose(): void;
}

async function createGltfStack(renderer: WebGLRenderer): Promise<GltfStack> {
  const [{ GLTFLoader }, { DRACOLoader }, { KTX2Loader }] = await Promise.all([
    import('three/addons/loaders/GLTFLoader.js'),
    import('three/addons/loaders/DRACOLoader.js'),
    import('three/addons/loaders/KTX2Loader.js'),
  ]);

  const draco = new DRACOLoader().setDecoderPath(DRACO_PATH);
  const ktx2 = new KTX2Loader().setTranscoderPath(KTX2_PATH).detectSupport(renderer);
  const gltf = new GLTFLoader().setDRACOLoader(draco).setKTX2Loader(ktx2);

  return {
    async load(url: string) {
      const result = await gltf.loadAsync(url);
      return result.scene;
    },
    dispose() {
      draco.dispose();
      ktx2.dispose();
    },
  };
}

export interface Loader {
  loadAll(): Promise<LoadedAssets>;
  dispose(): void;
}

export interface LoaderOptions {
  /** Test/diagnostic escape hatch; measured buildings load by default. */
  loadBuildings?: boolean;
}

function isTerrainGridData(value: unknown): value is TerrainGridData {
  if (!value || typeof value !== 'object') return false;
  const data = value as Partial<TerrainGridData>;
  return typeof data.cellM === 'number' && data.cellM > 0 &&
    typeof data.cols === 'number' && data.cols >= 2 &&
    typeof data.rows === 'number' && data.rows >= 2 &&
    typeof data.gapRatio === 'number' && data.gapRatio <= 0.01 &&
    Array.isArray(data.heights) && data.heights.length === data.cols * data.rows;
}

export function createLoader(
  renderer: WebGLRenderer,
  onProgress?: LoadProgress,
  { loadBuildings = true }: LoaderOptions = {},
): Loader {
  let stack: GltfStack | null = null;

  /** Cheap existence probe. Costs one HEAD per asset and pulls in no decoders. */
  async function exists(url: string): Promise<boolean> {
    try {
      const res = await fetch(url, { method: 'HEAD', cache: 'no-cache' });
      if (!res.ok) return false;
      // SPA dev/static hosts commonly answer an unknown path with index.html
      // and status 200. Treating that as a GLB pulled in ~1.5 MB of decoders
      // and then tried to parse HTML three times on every procedural boot.
      const contentType = res.headers.get('content-type')?.toLowerCase() ?? '';
      return !contentType.includes('text/html');
    } catch {
      return false;
    }
  }

  return {
    async loadAll(): Promise<LoadedAssets> {
      onProgress?.(0.1, '檢查資產…');
      const names = (Object.keys(ASSETS) as AssetName[]).filter(
        (name) => (name !== 'road' || BAKED_ROAD_ENABLED) && (name !== 'buildings' || loadBuildings),
      );
      const present = await Promise.all(names.map((n) => exists(ASSETS[n])));
      const wanted = names.filter((_, i) => present[i]);

      const out: LoadedAssets = { road: null, mid: null, far: null, props: null, buildings: null, terrain: null };
      if (wanted.length === 0) {
        onProgress?.(1, '未有預烘資產，使用程序化道路');
        return out;
      }

      const glbNames = wanted.filter((name) => name !== 'buildings' && name !== 'terrain');
      if (glbNames.length > 0) {
        onProgress?.(0.3, '載入解碼器…');
        stack = await createGltfStack(renderer);
      }

      let done = 0;
      await Promise.all(
        wanted.map(async (name) => {
          try {
            if (name === 'buildings' || name === 'terrain') {
              const response = await fetch(ASSETS[name], { cache: 'no-cache' });
              if (!response.ok) throw new Error(`HTTP ${response.status}`);
              const json: unknown = await response.json();
              if (name === 'buildings') {
                if (!isBuildingCollection(json)) throw new Error('invalid building GeoJSON');
                out.buildings = json;
              } else {
                if (!isTerrainGridData(json)) throw new Error('invalid measured terrain grid');
                out.terrain = json;
              }
            } else {
              const scene = await stack!.load(ASSETS[name]);
              scene.name = name;
              out[name] = scene;
            }
          } catch (err) {
            // A malformed asset must degrade to the procedural fallback, never
            // take the whole app down.
            console.warn(`[loader] ${name} present but failed to parse:`, err);
          }
          done++;
          onProgress?.(0.3 + (done / wanted.length) * 0.7, `載入 ${name}…`);
        }),
      );
      return out;
    },
    dispose() {
      stack?.dispose();
      stack = null;
    },
  };
}

function isBuildingCollection(value: unknown): value is BuildingFeatureCollection {
  return !!value && typeof value === 'object' &&
    (value as { type?: unknown }).type === 'FeatureCollection' &&
    Array.isArray((value as { features?: unknown }).features);
}
